import { useQuery } from '@tanstack/react-query'
import {
  Chip,
  CircularProgress,
  Dialog, DialogContent, DialogTitle,
  IconButton,
  Table, TableBody, TableCell, TableHead, TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'

import { governanceApi } from '../services/governance-api'

interface IProps {
  workspaceId: string
  open: boolean
  onClose(): void
}

const ORIGIN_COLOR: Record<string, 'default' | 'warning' | 'info'> = {
  excecao: 'info',
  manual: 'warning',
}

/**
 * Vigências alteradas drill-down dialog. Pattern B — full PII.
 * Lists movements whose VIGENCY_END_DATE diverges from PLAN_EXCLUSION_DATE.
 */
export function VigenciasDrilldownDialog({ workspaceId, open, onClose }: IProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['governance', 'vigencias-drilldown', workspaceId],
    queryFn: () => governanceApi.vigenciasDrilldown({ workspaceId, limit: 200 }),
    enabled: open && !!workspaceId,
  })

  const rows = data?.data.rows ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">Vigências alteradas</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <CircularProgress size={20} />}
        {isError && <Typography color="error">Erro ao carregar vigências alteradas.</Typography>}
        {data && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>CPF</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell>Vigência fim</TableCell>
                <TableCell>Default (cálculo)</TableCell>
                <TableCell align="right">Δ dias</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Origem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={`${r.person_tax_payer_id}-${i}`}>
                  <TableCell>{r.person_tax_payer_id}</TableCell>
                  <TableCell>{r.person_name ?? '—'}</TableCell>
                  <TableCell>{r.plan ?? '—'}</TableCell>
                  <TableCell>{r.vigency_end_date ? dayjs(r.vigency_end_date).format('DD/MM/YYYY') : '—'}</TableCell>
                  <TableCell>{r.default_exclusion_date ? dayjs(r.default_exclusion_date).format('DD/MM/YYYY') : '—'}</TableCell>
                  <TableCell align="right">{r.days_diff ?? '—'}</TableCell>
                  <TableCell>{r.status ?? '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.change_origin} color={ORIGIN_COLOR[r.change_origin] ?? 'default'} />
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">Nenhuma vigência alterada.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}
